<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.activation_status') }}
              </span>
            </p>
            <b-select v-model="filterStatus" @input="fetchData">
              <option value="">{{ $t('attributes.all') }}</option>
              <option value="true">{{ $t('attributes.enabled') }}</option>
              <option value="false">{{ $t('attributes.disabled') }}</option>
            </b-select>
          </b-field>
          <b-field>
            <b-input
              v-model="search"
              placeholder="Search..."
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
            <p class="control">
              <button class="button is-primary" @click="fetchData">
                {{ $t('attributes.search') }}
              </button>
            </p>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            icon-left="plus"
            @click="onAdd"
          >
            {{ $t('attributes.add') + $t('attributes.collaborate') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :selected.sync="selectedItem"
      :loading="isLoading"
      :hoverable="true"
      :narrowed="true"
      :mobile-cards="true"
      paginated
      backend-pagination
      backend-sorting
      :total="total"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="active"
        :label="$t('attributes.activation_status')"
        width="95"
        sortable
        v-slot="props"
      >
        <b-icon :icon="props.row.active ? 'check' : 'close'"></b-icon>
      </b-table-column>

      <b-table-column
        field="id"
        :label="$t('attributes.id')"
        width="95"
        numeric
        sortable
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="logo"
        :label="$t('attributes.logo')"
        width="100"
        v-slot="props"
      >
        <img
          v-if="props.row.logo"
          :src="collaborateImage(props.row.logo)"
          alt=""
          style="height: 40px;"
        />
      </b-table-column>

      <b-table-column
        field="name"
        :label="$t('attributes.name')"
        style="min-width: 200px;"
        sortable
        v-slot="props"
      >
        <p style="white-space: nowrap;">
          {{ props.row.name }}
        </p>
      </b-table-column>

      <b-table-column
        field="vat"
        :label="$t('attributes.vat')"
        style="min-width: 100px"
        sortable
        v-slot="props"
      >
        {{ props.row.vat }}
      </b-table-column>

      <b-table-column label="報名網址" centered v-slot="props">
        <div style="white-space: nowrap;">
          <a
            :href="collaborateUrl(props.row.id)"
            target="_blank"
            class="underline"
          >
            連結
          </a>
          <b-button
            type="is-ghost"
            icon-left="content-copy"
            style="color: #9e6305; font-size: 0.5rem"
            @click="onCopyCollaborateUrl(props.row.id)"
          ></b-button>
        </div>
      </b-table-column>

      <b-table-column
        field="tel"
        :label="$t('attributes.tel')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.tel }}
      </b-table-column>

      <b-table-column
        field="service_tel"
        :label="$t('attributes.service_tel')"
        width="170"
        sortable
        v-slot="props"
      >
        {{ props.row.service_tel }}
      </b-table-column>

      <b-table-column
        :label="$t('attributes.function')"
        width="210"
        style="min-width: 200px"
        centered
        v-slot="props"
      >
        <div class="buttons">
          <b-button type="is-success" @click.prevent.stop="onModify(props.row)">
            {{ $t('attributes.modify') }}
          </b-button>
          <b-button
            type="is-info"
            @click.prevent.stop="onModifyContacts(props.row)"
          >
            {{ $t('attributes.contacts') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
            <p>{{ $t('attributes.empty') }}</p>
          </div>
        </section>
      </template>
    </b-table>

    <section v-if="selectedItem">
      <collaborate-users :collaborate="selectedItem" />
    </section>

    <b-modal
      :active.sync="isModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <collaborate-form v-bind="selected" :sending="sending" @onSend="onSend" />
    </b-modal>

    <b-modal
      :active.sync="isContactsModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <contacts-form v-bind="selected" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'
import { FIRM_COLLABORATES_API } from '@/utils/apis'
import CollaborateForm from '@/components/firm/CollaborateForm'
import ContactsForm from '@/components/firm/ContactsForm'
import CollaborateUsers from '@/components/firm/CollaborateUsers'

export default {
  name: 'Collaborates',
  components: { ContactsForm, CollaborateForm, CollaborateUsers },

  data() {
    return {
      isLoading: false,
      items: [],
      total: 0,
      filterStatus: '',
      search: '',
      sortField: 'id',
      sortOrder: 'asc',
      defaultSortOrder: 'asc',
      page: 1,
      perPage: 10,

      selectedItem: null,

      selected: null,
      isModalActive: false,
      isContactsModalActive: false,
      sending: false,
      isEditing: false
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      const api = FIRM_COLLABORATES_API

      const params = {
        active: this.filterStatus && this.filterStatus === 'true',
        search: this.search,
        sort_by: this.sortField,
        by_descending: this.sortOrder !== 'asc',
        page_size: this.perPage,
        page_number: this.page
      }

      this.isLoading = true
      try {
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('collaborate', e)
      } finally {
        this.isLoading = false
      }
    },
    onFilter: debounce(function() {
      this.fetchData()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.fetchData()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.fetchData()
    },
    onAdd() {
      this.isEditing = false
      this.selected = {}
      this.isModalActive = true
    },
    onModify(selected) {
      this.isEditing = true
      this.selected = selected
      this.isModalActive = true
    },
    onModifyContacts(selected) {
      this.selected = selected
      this.isContactsModalActive = true
    },
    onSend(item) {
      if (item.id == null) {
        this.createCollaborate(item)
      } else {
        this.updateCollaborate(item)
      }
    },
    async createCollaborate(item) {
      this.sending = true
      try {
        const res = await this.axios.post(FIRM_COLLABORATES_API, item)
        const _item = res.data.data
        this.items = [...this.items, _item]
        this.isModalActive = false

        notifyService.success(this.$t('attributes.add_succeed'))
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
      }
    },
    async updateCollaborate(item) {
      const api = `${FIRM_COLLABORATES_API}/${item.id}`

      this.sending = true
      try {
        const res = await this.axios.put(api, item)
        const _item = res.data.data

        const idx = this.items.findIndex(s => s.id === _item.id)
        const items = [...this.items]
        items[idx] = _item

        this.items = items
        this.isModalActive = false

        notifyService.success(this.$t('attributes.update_succeed'))
      } catch (e) {
        notifyService.update_error(e)
      } finally {
        this.sending = false
      }
    },
    collaborateUrl(id) {
      return `${process.env.VUE_APP_COLLABORATE_URL}/login?c=${id}`
    },
    async onCopyCollaborateUrl(id) {
      const url = this.collaborateUrl(id)

      await navigator.clipboard.writeText(url)

      return notifyService.success('連結已複製到剪貼簿。')
    }
  }
}
</script>
